/**
 * 路由守卫 — 认证拦截 + 权限检查
 * 1. 白名单路由：/login, /sidebar
 * 2. Token 检查：无 Token → /login，有 Token + /login → /
 * 3. 权限检查：无权限访问的模块 → 重定向到首页
 */
import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { PSI_ACCESS_DISABLED_EVENT, usePsiAccessStore } from '@/stores/psiAccess'
import {
  CUSTOMER_BUY_ANALYSIS_FEATURE_ENABLED,
  PSI_SHIPPING_INTEGRATION_FEATURE_ENABLED,
} from '@/constants/psiRelease'

/** 无需认证的白名单路由 */
const AUTH_WHITELIST = ['/login', '/sidebar']

const RELEASE_DISABLED_ROUTE_NAMES = new Set([
  ...(!CUSTOMER_BUY_ANALYSIS_FEATURE_ENABLED ? ['CustomerBuyAnalysis'] : []),
  ...(!PSI_SHIPPING_INTEGRATION_FEATURE_ENABLED ? ['WarehouseEorderShipping'] : []),
])

/** 路由权限映射（与 SideNav.vue 中的 permission 保持一致） */
const ROUTE_PERMISSION_MAP: Record<string, string | string[]> = {
  MarketingTargets: 'client:marketing_target:view',
  MarketingTargetCreate: 'client:marketing_target:view',
  MarketingTargetDecompose: 'client:marketing_target:view',
  MarketingTargetResult: 'client:marketing_target:view',
  MarketingTargetDetail: 'client:marketing_target:view',
  MarketingPkList: 'client:marketing_pk:view',
  MarketingPkCreate: 'client:marketing_pk:view',
  MarketingPkDetail: 'client:marketing_pk:view',
  MarketingPlanConfigs: ['marketingPlan:config:view', 'marketingPlan:config:manage'],
  MarketingPlanLogs: 'marketingPlan:log:view',
  Statistics: 'statistics:view',
  StatisticsWarehouseProductStats: 'warehouseProductStats:view',
  CustomerFirstRepeat: 'statistics:firstRepeat:view',
  WechatFirstRepeat: 'statistics:wechatFirstRepeat:view',
  CustomerFirstRepeatDistribution: 'statistics:firstRepeatDistribution:view',
  WechatSalesAnalysis: 'statistics:wechatSalesAnalysis:view',
  SalesGrossProfit: 'statistics:salesGrossProfit:view',
  RegionSalesStatistics: 'statistics:regionSales:view',
  FanHourlyStatistics: 'statistics:fanHourly:view',
  FanDailyStatistics: 'statistics:fanDaily:view',
  WechatFanDailyStatistics: 'statistics:wechatFanDaily:view',
  SalesDepartmentRanking: 'statistics:salesDepartmentRanking:view',
  StatisticsSalesPerformanceShare: 'statistics:salesPerformanceShare:view',
  ServiceMonthlyRanking: 'statistics:serviceMonthlyRanking:view',
  ServiceFirstRepeatNetValue: 'statistics:serviceFirstRepeatNetValue:view',
  ProjectReport: 'project:report:view',
  OperationsProjectReport: 'project:report:view',
  ProjectOperationBrief: 'salesOrder:operationBrief:view',
  Tasks: 'task:view',
  TaskDetail: 'task:view',
  Leads: 'leads:view',
  ApprovalList: ['approval:view', 'approval:action'],
  ApprovalFlowPrototype: ['approvalFlow:view', 'approvalFlow:manage'],
  ApprovalDetail: ['approval:view', 'approval:action'],
  Customers: 'customer:view',
  CustomerDetail: 'customer:view',
  MessageNoticeConfigs: 'messageNotice:config:view',
  MessageNoticeLogs: 'messageNotice:log:view',
  WechatRiskContent: 'wechatRisk:content:view',
  WechatRiskEvents: 'wechatRisk:event:view',
  Products: 'product:info:view',
  ProductCategories: 'product:category:view',
  ProductSuppliers: 'product:supplier:view',
  PurchaseOrders: 'purchaseOrder:view',
  Credits: 'credit:view',
  AiConfigWorkbench: 'agentStudio:view',
  AiAgentStudio: 'agentStudio:view',
  AiKnowledge: 'agentStudioKnowledge:view',
  AiQaRecords: 'agentStudio:view',
  BrandList: 'brand:view',
  BrandCreate: 'brand:edit',
  BrandDetail: 'brand:view',
  KnowledgePhraseList: 'knowledgePhrase:view',
  Warehouse: 'warehouse:view',
  WarehouseProducts: 'warehouseProduct:view',
  WarehouseShippingWorkbench: 'warehouse:view',
  WarehouseLogisticsReconciliation: [
    'psi:logistics_reconciliation:view',
    'psi:logistics_reconciliation:settlement_audit',
    'psi:logistics_reconciliation:settlement_account_config',
    'psi:logistics_unsettled_order:view',
  ],
  FinanceLogisticsReconciliationImport: 'psi:logistics_reconciliation:view',
  FinanceLogisticsReconciliationAudit: [
    'psi:logistics_reconciliation:settlement_audit',
    'psi:logistics_reconciliation:settlement_account_config',
  ],
  FinanceLogisticsUnsettledOrder: 'psi:logistics_unsettled_order:view',
  WarehouseProductStats: 'warehouseProductStats:view',
  ProductInventoryStats: 'productInventoryStats:view',
  ProductInventoryDailyReport: 'productInventoryDailyReport:view',
  // WarehouseEorderShipping: 'warehouse:view',
  WarehouseDeliveryImport: 'warehouse:view',
  WarehouseShippingScan: 'warehouse:view',
  WarehouseShippingTaskDetail: 'warehouse:view',
  InventoryBills: 'inventoryBill:view',
  InventoryBillEdit: 'inventoryBill:view',
  InventoryBillDetail: 'inventoryBill:view',
  StockCheck: 'inventoryBill:view',
  StockCheckEdit: 'inventoryBill:view',
  InventoryLogs: 'inventoryLog:view',
  InventoryReports: 'inventoryReport:view',
  Orders: 'salesOrder:view',
  OperationBrief: 'salesOrder:operationBrief:view',
  PlatformStores: 'salesOrder:platformStore',
  PlatformOrderImport: 'salesOrder:platformImport',
  SalesOrderAudit: 'salesOrder:firstAudit',
  ShippingTransitChange: 'finance:shippingTransitChange:view',
  PaymentAudit: 'finance:paymentAudit',
  FinanceTradeReconciliation: 'finance:tradeReconciliation:view',
  CustomerPrestoreAudit: 'customer:prestore:audit:view',
  AfterSales: 'afterSales:view',
  AfterSalesAudit: 'afterSales:firstAudit',
  ShippingCarriers: 'shipping:carrier:view',
  ShippingApiConfig: 'shipping:api_config:view',
  ShippingSenderInfo: 'shipping:sender:view',
  OrgStructure: 'system:org',
  Roles: 'system:role',
  RoleEditor: 'system:role',
  UserOperationLogs: 'system:audit',
  CustomerServiceConfig: 'service:view',
  ProjectConfig: 'project:config',
  PaymentChannelSettings: 'finance:paymentChannel:view',
  ProfitFormulaConfig: 'biz:profit-formula:query',
  ReceiptAccountSettings: 'finance:receiptAccount:view',
  OrderBusinessSettingConfig: 'orderBusinessSetting:view',
  OrderTypeSettings: 'orderType:view',
  DealCycleSettings: 'dealCycle:view',
  CustomerGroupSettings: 'customerGroup:view',
  // 'AuditLog': 'system:audit',
}

/** 管理类页面额外要求操作权限，必须与上面的页面访问权限同时满足。 */
const ROUTE_OPERATION_PERMISSION_MAP: Record<string, string> = {
  MarketingTargetCreate: 'client:marketing_target:manage',
  MarketingTargetDecompose: 'client:marketing_target:manage',
  MarketingPkCreate: 'client:marketing_pk:manage',
}

/**
 * 登录后默认首页 /dashboard，显式合法回跳优先
 * 找到第一个用户有权限的路由跳转，全无 → 视为账号未分配任何业务权限
 */
const ROUTE_FALLBACK_ORDER: Array<{ name: string; path: string }> = [
  { name: 'Dashboard', path: '/dashboard' },
  { name: 'Tasks', path: '/tasks' },
  { name: 'ApprovalList', path: '/tasks/approval' },
  { name: 'ApprovalFlowPrototype', path: '/tasks/approval/flows-prototype' },
  { name: 'Customers', path: '/customers' },
  { name: 'Statistics', path: '/statistics' },
  { name: 'OperationBrief', path: '/orders/operation-brief' },
  { name: 'CustomerFirstRepeat', path: '/statistics/customer-first-repeat' },
  { name: 'WechatFirstRepeat', path: '/statistics/wechat-first-repeat' },
  {
    name: 'CustomerFirstRepeatDistribution',
    path: '/statistics/customer-first-repeat-distribution',
  },
  { name: 'WechatSalesAnalysis', path: '/statistics/wechat-sales-analysis' },
  { name: 'SalesGrossProfit', path: '/statistics/sales-gross-profit' },
  { name: 'FanHourlyStatistics', path: '/statistics/fan-hourly' },
  { name: 'FanDailyStatistics', path: '/statistics/fan-daily' },
  { name: 'WechatFanDailyStatistics', path: '/statistics/wechat-fan-daily' },
  {
    name: 'StatisticsSalesPerformanceShare',
    path: '/statistics/sales-performance-share',
  },
  { name: 'Products', path: '/products' },
  { name: 'ProductCategories', path: '/products/categories' },
  { name: 'ProductSuppliers', path: '/products/suppliers' },
  { name: 'PurchaseOrders', path: '/products/purchase-orders' },
  { name: 'Credits', path: '/credits' },
  { name: 'Warehouse', path: '/warehouse' },
  { name: 'WarehouseProducts', path: '/warehouse/products' },
  { name: 'WarehouseProductStats', path: '/warehouse/product-stats' },
  { name: 'ProductInventoryStats', path: '/warehouse/product-inventory-stats' },
  { name: 'ProductInventoryDailyReport', path: '/warehouse/product-inventory-daily-report' },
  { name: 'WarehouseShippingWorkbench', path: '/warehouse/shipping-workbench' },
  {
    name: 'FinanceLogisticsReconciliationImport',
    path: '/finance/logistics-reconciliation/import',
  },
  { name: 'WarehouseShippingScan', path: '/warehouse/scan-check' },
  { name: 'InventoryBills', path: '/warehouse/inventory-bills' },
  { name: 'StockCheck', path: '/warehouse/stock-check' },
  { name: 'InventoryLogs', path: '/warehouse/inventory-logs' },
  { name: 'InventoryReports', path: '/warehouse/inventory-reports' },
  { name: 'Orders', path: '/orders' },
  { name: 'FinanceTradeReconciliation', path: '/finance/trade-reconciliation' },
  { name: 'PlatformStores', path: '/orders/platform-stores' },
  { name: 'PlatformOrderImport', path: '/orders/platform-import' },
  { name: 'SalesOrderAudit', path: '/tasks/approval?businessType=sales_order' },
  { name: 'AfterSales', path: '/after-sales' },
  { name: 'AfterSalesAudit', path: '/tasks/approval?businessType=after_sales' },
  { name: 'ShippingCarriers', path: '/shipping/settings/carriers' },
  { name: 'ShippingApiConfig', path: '/shipping/settings/api-config' },
  { name: 'OrgStructure', path: '/system/org' },
  { name: 'Roles', path: '/system/roles' },
  { name: 'AiConfigWorkbench', path: '/config/ai-workbench' },
  { name: 'UserOperationLogs', path: '/system/user-operation-logs' },
  { name: 'CustomerServiceConfig', path: '/config/customer-service' },
  { name: 'ProjectConfig', path: '/config/project' },
  { name: 'PaymentChannelSettings', path: '/finance/payment-channels' },
  { name: 'ReceiptAccountSettings', path: '/finance/receipt-accounts' },
  { name: 'OrderBusinessSettingConfig', path: '/config/order-business-setting' },
  { name: 'OrderTypeSettings', path: '/config/order-types' },
  { name: 'DealCycleSettings', path: '/config/deal-cycle-settings' },
  { name: 'MarketingPlanConfigs', path: '/marketing-plan/configs' },
  { name: 'MarketingPlanLogs', path: '/marketing-plan/logs' },
]

/** 在 fallback 顺序中找到第一个当前用户能访问的路由路径，找不到返回 null */
function pathRequiresPsi(router: Router, path: string): boolean {
  return router.resolve(path).matched.some((record) => record.meta.requiresPsi === true)
}

function hasPathAccess(router: Router, routeName: string, path: string): boolean {
  return hasRouteAccess(routeName, pathRequiresPsi(router, path))
}

function findFallbackPath(router: Router): string | null {
  for (const r of ROUTE_FALLBACK_ORDER) {
    if (hasPathAccess(router, r.name, r.path)) return r.path
  }
  return null
}

function findProductFallbackPath(router: Router): string | null {
  if (hasPathAccess(router, 'Products', '/products')) return '/products'
  if (hasPathAccess(router, 'ProductCategories', '/products/categories')) {
    return '/products/categories'
  }
  if (hasPathAccess(router, 'ProductSuppliers', '/products/suppliers')) {
    return '/products/suppliers'
  }
  if (hasPathAccess(router, 'PurchaseOrders', '/products/purchase-orders')) {
    return '/products/purchase-orders'
  }
  return null
}

function findOrderFallbackPath(router: Router): string | null {
  if (hasPathAccess(router, 'Orders', '/orders')) return '/orders'
  if (hasPathAccess(router, 'PlatformStores', '/orders/platform-stores')) {
    return '/orders/platform-stores'
  }
  if (hasPathAccess(router, 'PlatformOrderImport', '/orders/platform-import')) {
    return '/orders/platform-import'
  }
  return null
}

function findFinanceFallbackPath(router: Router): string | null {
  if (
    hasPathAccess(
      router,
      'FinanceLogisticsReconciliationImport',
      '/finance/logistics-reconciliation/import',
    )
  ) {
    return '/finance/logistics-reconciliation/import'
  }
  if (
    hasPathAccess(
      router,
      'FinanceLogisticsReconciliationAudit',
      '/finance/logistics-reconciliation/audit',
    )
  ) {
    return '/finance/logistics-reconciliation/audit'
  }
  if (
    hasPathAccess(
      router,
      'FinanceLogisticsUnsettledOrder',
      '/finance/logistics-reconciliation/unsettled',
    )
  ) {
    return '/finance/logistics-reconciliation/unsettled'
  }
  return null
}

function findWarehouseFallbackPath(router: Router): string | null {
  if (hasPathAccess(router, 'Warehouse', '/warehouse')) return '/warehouse'
  if (hasPathAccess(router, 'WarehouseProducts', '/warehouse/products')) {
    return '/warehouse/products'
  }
  if (hasPathAccess(router, 'WarehouseProductStats', '/warehouse/product-stats')) {
    return '/warehouse/product-stats'
  }
  if (hasPathAccess(router, 'ProductInventoryStats', '/warehouse/product-inventory-stats')) {
    return '/warehouse/product-inventory-stats'
  }
  if (
    hasPathAccess(
      router,
      'ProductInventoryDailyReport',
      '/warehouse/product-inventory-daily-report',
    )
  ) {
    return '/warehouse/product-inventory-daily-report'
  }
  if (hasPathAccess(router, 'WarehouseShippingWorkbench', '/warehouse/shipping-workbench')) {
    return '/warehouse/shipping-workbench'
  }
  if (hasPathAccess(router, 'WarehouseShippingScan', '/warehouse/scan-check')) {
    return '/warehouse/scan-check'
  }
  if (hasPathAccess(router, 'InventoryBills', '/warehouse/inventory-bills')) {
    return '/warehouse/inventory-bills'
  }
  if (hasPathAccess(router, 'StockCheck', '/warehouse/stock-check')) {
    return '/warehouse/stock-check'
  }
  if (hasPathAccess(router, 'InventoryLogs', '/warehouse/inventory-logs')) {
    return '/warehouse/inventory-logs'
  }
  if (hasPathAccess(router, 'InventoryReports', '/warehouse/inventory-reports')) {
    return '/warehouse/inventory-reports'
  }
  return null
}

function findStatisticsFallbackPath(router: Router): string | null {
  if (hasPathAccess(router, 'Statistics', '/statistics')) return '/statistics'
  if (hasPathAccess(router, 'OperationBrief', '/orders/operation-brief')) {
    return '/orders/operation-brief'
  }
  if (hasPathAccess(router, 'CustomerFirstRepeat', '/statistics/customer-first-repeat')) {
    return '/statistics/customer-first-repeat'
  }
  if (hasPathAccess(router, 'WechatFirstRepeat', '/statistics/wechat-first-repeat')) {
    return '/statistics/wechat-first-repeat'
  }
  if (
    hasPathAccess(
      router,
      'CustomerFirstRepeatDistribution',
      '/statistics/customer-first-repeat-distribution',
    )
  ) {
    return '/statistics/customer-first-repeat-distribution'
  }
  if (hasPathAccess(router, 'WechatSalesAnalysis', '/statistics/wechat-sales-analysis')) {
    return '/statistics/wechat-sales-analysis'
  }
  if (hasPathAccess(router, 'SalesGrossProfit', '/statistics/sales-gross-profit')) {
    return '/statistics/sales-gross-profit'
  }
  if (hasPathAccess(router, 'FanHourlyStatistics', '/statistics/fan-hourly')) {
    return '/statistics/fan-hourly'
  }
  if (hasPathAccess(router, 'FanDailyStatistics', '/statistics/fan-daily')) {
    return '/statistics/fan-daily'
  }
  if (hasPathAccess(router, 'WechatFanDailyStatistics', '/statistics/wechat-fan-daily')) {
    return '/statistics/wechat-fan-daily'
  }
  if (hasPathAccess(router, 'SalesDepartmentRanking', '/statistics/sales-department-ranking')) {
    return '/statistics/sales-department-ranking'
  }
  if (
    hasPathAccess(router, 'StatisticsSalesPerformanceShare', '/statistics/sales-performance-share')
  ) {
    return '/statistics/sales-performance-share'
  }
  if (hasPathAccess(router, 'ServiceMonthlyRanking', '/statistics/service-monthly-ranking')) {
    return '/statistics/service-monthly-ranking'
  }
  if (
    hasPathAccess(
      router,
      'ServiceFirstRepeatNetValue',
      '/statistics/service-first-repeat-net-value',
    )
  ) {
    return '/statistics/service-first-repeat-net-value'
  }
  return null
}

/**
 * 检查用户是否有权限访问该路由
 * - 超级管理员拥有所有权限
 * - 根据路由 name 查找对应的权限码
 * - 管理类页面还需满足对应操作权限，不替代页面访问权限
 * - 无权限码配置的路由默认允许访问
 * @param routeName 目标路由名称，用于匹配页面及操作权限。
 * @param requiresPsi 是否需要校验 PSI 功能已开启。
 */
function hasRouteAccess(routeName: string | undefined | null, requiresPsi = false): boolean {
  const authStore = useAuthStore()
  const psiAccessStore = usePsiAccessStore()

  if (requiresPsi && !psiAccessStore.isEnabled) return false
  if (!routeName) return true
  if (RELEASE_DISABLED_ROUTE_NAMES.has(routeName)) return false

  // 超级管理员拥有所有权限
  if (authStore.isSuperAdmin) return true

  // 操作权限只作附加限制，不能单独放行没有页面访问权限的账号。
  const operationPermission = ROUTE_OPERATION_PERMISSION_MAP[routeName]
  if (operationPermission && !authStore.hasPermission(operationPermission)) return false

  // 查找该路由需要的权限码
  const requiredPermission = ROUTE_PERMISSION_MAP[routeName]
  if (!requiredPermission) return true // 未配置权限码的路由默认允许

  // 检查权限
  return Array.isArray(requiredPermission)
    ? requiredPermission.some((permission) => authStore.hasPermission(permission))
    : authStore.hasPermission(requiredPermission)
}

export function setupRouterGuards(router: Router) {
  router.beforeEach(async (to, _from, next) => {
    const isWhitelisted = AUTH_WHITELIST.includes(to.path)

    // 侧边栏路由不做认证拦截
    if (to.path === '/sidebar') {
      next()
      return
    }

    const authStore = useAuthStore()
    const hasToken = authStore.isLoggedIn
    const psiAccessStore = usePsiAccessStore()
    const requiresPsi = to.matched.some((record) => record.meta.requiresPsi === true)
    if (hasToken && requiresPsi && psiAccessStore.status === 'unknown') {
      try {
        await psiAccessStore.refresh()
      } catch {
        // Store 保持 unknown，下面按 fail-closed 处理且不影响登录态。
      }
    }
    const deniedByPsi = hasToken && requiresPsi && !psiAccessStore.isEnabled

    if (!isWhitelisted && !hasToken) {
      // 未登录 → 跳转登录页
      next('/login')
    } else if (to.path === '/login' && hasToken) {
      // 已登录访问登录页 → 跳工作台首页；
      // 首页保留受限模块占位，不要求其他业务菜单权限
      next('/dashboard')
    } else if (hasToken && !hasRouteAccess(to.name as string, deniedByPsi)) {
      // 已登录但无权限访问该路由 → 在 fallback 顺序中找一个可访问的，避免重定向死循环
      console.warn(`[Router Guard] 无权限访问: ${to.path} (${String(to.name)})`)
      // 项目运营简报保留历史 URL，但拒绝访问时按运营分析优先寻找可访问页面。
      if (to.path.startsWith('/marketing-plan')) {
        const marketingFallback = ROUTE_FALLBACK_ORDER.find(
          (item) =>
            item.path.startsWith('/marketing-plan/') && hasPathAccess(router, item.name, item.path),
        )
        if (marketingFallback && marketingFallback.path !== to.path) {
          next({ path: marketingFallback.path, query: to.query })
          return
        }
      }
      if (to.path.startsWith('/statistics') || to.name === 'OperationBrief') {
        const statisticsFallback = findStatisticsFallbackPath(router)
        if (statisticsFallback && statisticsFallback !== to.path) {
          next(statisticsFallback)
          return
        }
      }
      if (to.path.startsWith('/products')) {
        const productFallback = findProductFallbackPath(router)
        if (productFallback && productFallback !== to.path) {
          next(productFallback)
          return
        }
      }
      if (to.path.startsWith('/finance')) {
        const financeFallback = findFinanceFallbackPath(router)
        if (financeFallback && financeFallback !== to.path) {
          next(financeFallback)
          return
        }
      }
      if (to.path.startsWith('/warehouse')) {
        const warehouseFallback = findWarehouseFallbackPath(router)
        if (warehouseFallback && warehouseFallback !== to.path) {
          next(warehouseFallback)
          return
        }
      }
      if (to.path.startsWith('/orders')) {
        const orderFallback = findOrderFallbackPath(router)
        if (orderFallback && orderFallback !== to.path) {
          next(orderFallback)
          return
        }
      }
      const fallback = findFallbackPath(router)
      if (fallback && fallback !== to.path) {
        next(fallback)
      } else if (deniedByPsi) {
        next('/settings/diagnostic')
        return
      } else {
        // 全无业务权限或 fallback 等于当前路径（会循环）→ 清登录态回登录页并提示
        console.warn('[Router Guard] 无任何可访问的业务路由，登出并回登录页')
        // 先同步清掉前端 token，避免异步 logout 完成前 /login 又被判定为已登录而跳回首页。
        authStore.clearAuth()
        authStore.logout().catch((e) => {
          console.warn('[Router Guard] 退出登录失败:', e)
        })
        next({ path: '/login', query: { reason: 'no_permission' } })
        return
      }
    } else {
      next()
    }
  })

  window.addEventListener(PSI_ACCESS_DISABLED_EVENT, () => {
    const currentRoute = router.currentRoute.value
    const requiresPsi = currentRoute.matched.some((record) => record.meta.requiresPsi === true)
    if (!requiresPsi) return
    const fallback = findFallbackPath(router) ?? '/settings/diagnostic'
    if (fallback !== currentRoute.fullPath) {
      router.replace(fallback).catch((error) => {
        console.warn('[Router Guard] PSI 停用后返回公共页面失败:', error)
      })
    }
  })
}
